import { useEffect, useMemo, useState } from 'react'
import { BellRing, Eye, List } from 'lucide-react'
import DataTable from './DataTable'
import IconButton from './IconButton'
import Modal from './Modal'
import StatusBadge from './StatusBadge'
import { notificationStates as STATE_LABEL, notifications as S } from './strings'
import { listObjectBatches } from '../features/systemLogs/systemLogsService'
import './NotificationsModal.css'

/**
 * The shared `_MessagesModal.cshtml` partial — the notification batches behind a
 * record, reached from the Invitations row action and the group email modal.
 *
 * The legacy nests three modals (batches → messages → JSON). This keeps two: the batches
 * behind the record, and what happened to each message in one of them — which is the part
 * an operator opens this for. "Finished" means the RUN completed; only the message says
 * whether the address was reached.
 *
 * It read a bundled fixture until this endpoint existed, and that fixture was emptied for
 * shipping recipient addresses in the client bundle — so every record, however much history
 * it had, showed "no matching records found".
 */

const STATE_TONE = {
  Waiting: 'warning',
  Running: 'primary',
  Stopped: 'dark',
  Finished: 'success',
  Failed: 'danger',
  Succeeded: 'success',
}

const asCell = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '')

export default function NotificationsModal({ open, objectId, templateTypes = [], onClose }) {
  const [detail, setDetail] = useState(null)
  const [batches, setBatches] = useState(null)
  const [loadError, setLoadError] = useState(null)

  /* The types are an array literal at every call site, so a new identity on every render
     of the screen behind this modal — depending on it directly would refetch for ever. */
  const typeKey = templateTypes.join(',')

  useEffect(() => {
    if (!open) {
      setDetail(null)
      return undefined
    }

    let cancelled = false
    setBatches(null)
    setLoadError(null)

    // On open, and scoped to this record — the server filters by object and type.
    listObjectBatches({ objectId, templateTypes: typeKey ? typeKey.split(',').map(Number) : [] })
      .then((rows) => {
        if (!cancelled) setBatches(rows)
      })
      .catch((error) => {
        if (cancelled) return
        // A failed load is not an empty history; the table says so rather than
        // reporting "no records" for a server fault.
        setLoadError(error)
        setBatches([])
      })

    return () => {
      cancelled = true
    }
  }, [open, objectId, typeKey])

  const rows = batches ?? []

  const columns = useMemo(
    () => [
      {
        key: 'state',
        header: S.state,
        value: (row) => STATE_LABEL[row.state] ?? row.state,
        render: (row) => (
          <StatusBadge tone={STATE_TONE[row.state] ?? 'dark'}>
            {STATE_LABEL[row.state] ?? row.state}
          </StatusBadge>
        ),
      },
      {
        key: 'scheduledStartDate',
        header: S.scheduledOn,
        render: (row) => (
          <span className="notifications-modal__date" dir="ltr">
            {asCell(row.scheduledStartDate)}
          </span>
        ),
      },
      {
        key: 'startDate',
        header: S.startDate,
        render: (row) => (
          <span className="notifications-modal__date" dir="ltr">
            {asCell(row.startDate)}
          </span>
        ),
      },
      {
        key: 'endDate',
        header: S.endDate,
        render: (row) => (
          <span className="notifications-modal__date" dir="ltr">
            {asCell(row.endDate)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: <List size={15} strokeWidth={1.75} />,
        align: 'center',
        width: '80px',
        sortable: false,
        searchable: false,
        render: (row) => (
          <IconButton icon={Eye} label={S.details} tone="info" onClick={() => setDetail(row)} />
        ),
      },
    ],
    [],
  )

  return (
    <>
      <Modal open={open} icon={BellRing} size="full" title={S.title} onClose={onClose}>
        <DataTable
          /* A list inside a sheet: no page of its own in the address — it was writing into the
             `?page` of the screen behind it — and it starts at the top each time. */
          stateKey={false}
          columns={columns}
          rows={rows}
          error={loadError}
          loading={batches === null}
          getRowId={(row) => row.id}
          initialSort={{ key: 'scheduledStartDate', dir: 'desc' }}
        />
      </Modal>

      <Modal open={!!detail} size="lg" title={S.details} onClose={() => setDetail(null)}>
        {detail?.messages?.length ? (
          <ul className="notifications-modal__messages">
            {detail.messages.map((message) => (
              <li key={message.id}>
                <span className="notifications-modal__recipient">
                  <bdi>{message.receiver || message.channel}</bdi>
                </span>
                <StatusBadge tone={STATE_TONE[message.deliveryStatus] ?? 'dark'}>
                  {STATE_LABEL[message.deliveryStatus] ?? message.deliveryStatus}
                </StatusBadge>
                {/* Only when it took more than one go — a quiet 1 on every row is noise. */}
                {message.tries > 1 && (
                  <span className="notifications-modal__tries">
                    {S.tries}: {message.tries}
                  </span>
                )}
                {message.error && (
                  <span className="notifications-modal__error">
                    <bdi>{message.error}</bdi>
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          /* A batch can finish having produced nothing — no recipient resolved to an
             address — and that is worth saying rather than showing an empty box. */
          <p className="notifications-modal__empty">{S.noMessages}</p>
        )}
      </Modal>
    </>
  )
}
